import React, { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import { Check, X, Filter, Search, Info } from "lucide-react";
import "./PMSIntegrationsGrid.css";

// Define types for our component
interface PMS {
  id: string;
  name: string;
  code: string;
  status: "active" | "inactive" | "in progress";
}

interface Scope {
  id: string;
  name: string;
  code: string;
}

interface Endpoint {
  id: string;
  name: string;
  code: string;
  method: string;
  scope: string[]; // Array of scope codes
}

interface Connectivity {
  connector_code: string;
  actions_triggers_code: string;
  is_active: boolean;
  trigger_objects: string[] | null; // Array of scopes
}

interface PMSScope {
  code: string;
  actions_triggers_code: string[] | null;
}

// Interface for unique endpoints
interface UniqueEndpoint {
  id: string;
  name: string;
  code: string;
  method: string;
  scopes: string[]; // Array of scope codes that include this endpoint
}

// Interface for scope row in the grid
interface ScopeRow {
  code: string;
  name: string;
  endpoints: string[]; // Array of endpoint IDs that belong to this scope
}

export function PMSIntegrationsGrid() {
  const [pmsList, setPmsList] = useState<PMS[]>([]);
  // const [scopes, setScopes] = useState<Scope[]>([]);
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [uniqueEndpoints, setUniqueEndpoints] = useState<UniqueEndpoint[]>([]);
  const [scopeRows, setScopeRows] = useState<ScopeRow[]>([]);
  const [connectivities, setConnectivities] = useState<Connectivity[]>([]);
  const [pmsScopes, setPmsScopes] = useState<PMSScope[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedScope, setSelectedScope] = useState<string>("all");
  const [hoveredCell, setHoveredCell] = useState<{
    pms: string;
    scope: string;
    endpoint: string;
  } | null>(null);

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Load PMS systems (connectors)
        const { data: pmsData, error: pmsError } = await supabase
          .from("connectors")
          .select("id, name, code, status")
          .eq("type", "pms")
          .eq("status", "active")
          .order("name");

        if (pmsError) throw pmsError;
        setPmsList(pmsData || []);

        // For this example, we'll create mock endpoints data
        // In a real application, you would fetch this from your database
        const mockEndpoints: Endpoint[] = [
          {
            id: "2",
            name: "Get Hotel Info",
            code: "get_hotel_info",
            method: "GET",
            scope: ["crm", "bi", "rms"],
          },
          {
            id: "3",
            name: "Get Resource Categories",
            code: "get_resources_categories",
            method: "GET",
            scope: ["crm", "bi", "rms"],
          },
          {
            id: "4",
            name: "Get Products",
            code: "get_products",
            method: "GET",
            scope: ["crm", "bi", "rms"],
          },
          {
            id: "5",
            name: "Get Rate Plans",
            code: "get_rate_plans",
            method: "GET",
            scope: ["crm", "bi", "rms"],
          },
          {
            id: "6",
            name: "Get Company",
            code: "get_company",
            method: "GET",
            scope: ["crm", "bi", "rms"],
          },
          {
            id: "7",
            name: "Get Contact",
            code: "get_contact",
            method: "GET",
            scope: ["crm", "bi", "rms"],
          },
          {
            id: "8",
            name: "Search Reservations",
            code: "search_reservations",
            method: "GET",
            scope: ["crm", "bi", "rms"],
          },

          // BI and RMS shared endpoints
          {
            id: "9",
            name: "Get Charges",
            code: "get_charges",
            method: "GET",
            scope: ["bi", "rms"],
          },
          {
            id: "10",
            name: "Get Deposits",
            code: "get_deposits",
            method: "GET",
            scope: ["bi", "rms"],
          },

          // RMS-only endpoints
          {
            id: "11",
            name: "Get Inventory",
            code: "get_inventory",
            method: "GET",
            scope: ["rms"],
          },
          {
            id: "12",
            name: "Update Inventory",
            code: "update_inventory",
            method: "PUT",
            scope: ["rms"],
          },
          {
            id: "13",
            name: "Get Competitor Hotel Info",
            code: "get_competitor_info",
            method: "GET",
            scope: ["rms"],
          },
          {
            id: "14",
            name: "Get Competitor Rates",
            code: "get_competitor_rates",
            method: "GET",
            scope: ["rms"],
          },
        ];

        setEndpoints(mockEndpoints);

        // Process endpoints to create unique endpoints and scope rows
        const scopeMap: { [key: string]: string } = {
          crm: "CRM",
          bi: "BI",
          rms: "RMS",
        };

        // Sort endpoints by name for consistent order
        const sortedEndpoints = [...mockEndpoints].sort((a, b) => {
          return a.name.localeCompare(b.name);
        });

        // Create unique endpoints (already deduplicated in our new structure)
        const endpointMap = new Map<string, UniqueEndpoint>();

        sortedEndpoints.forEach((endpoint) => {
          const key = endpoint.name; // Use name as the key for uniqueness

          // Create a new unique endpoint directly from our new structure
          endpointMap.set(key, {
            id: endpoint.id,
            name: endpoint.name,
            code: endpoint.code,
            method: endpoint.method,
            scopes: endpoint.scope,
          });
        });

        const uniqueEndpointsList = Array.from(endpointMap.values());

        // Sort unique endpoints to ensure they're in a logical order
        // First group by which scopes they belong to, then by name
        uniqueEndpointsList.sort((a, b) => {
          // Sort by number of scopes (endpoints in more scopes come first)
          if (a.scopes.length !== b.scopes.length) {
            return b.scopes.length - a.scopes.length;
          }

          // Then sort by name
          return a.name.localeCompare(b.name);
        });

        setUniqueEndpoints(uniqueEndpointsList);

        // Create scope rows in the correct order: CRM, BI, RMS
        const scopeOrder = ["crm", "bi", "rms"];
        const scopeRowsList: ScopeRow[] = scopeOrder.map((scopeCode) => {
          // Get all endpoints for this scope
          const scopeEndpoints = mockEndpoints
            .filter((endpoint) => endpoint.scope.includes(scopeCode))
            .map((endpoint) => endpoint.id);

          return {
            code: scopeCode,
            name: scopeMap[scopeCode],
            endpoints: scopeEndpoints,
          };
        });

        setScopeRows(scopeRowsList);

        // Load connectivities data from the database
        const { data: connectivityData, error: connectivityError } =
          await supabase
            .from("connectivities")
            .select(
              "connector_code, actions_triggers_code, is_active, trigger_objects"
            );

        if (connectivityError) throw connectivityError;
        console.log("Connectivity data:", connectivityData);
        // Include all connectivities, not just active ones
        setConnectivities(connectivityData || []);

        // Load PMS scopes data from the database
        const { data: pmsScopesData, error: pmsScopesError } = await supabase
          .from("pms_scopes")
          .select("code, actions_triggers_code");

        if (pmsScopesError) throw pmsScopesError;
        console.log("PMS Scopes data:", pmsScopesData);
        setPmsScopes(pmsScopesData || []);
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filter PMS list based on search term
  const filteredPmsList = pmsList.filter((pms) =>
    pms.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter scope rows based on selected scope
  const filteredScopeRows =
    selectedScope === "all"
      ? scopeRows
      : scopeRows.filter((row) => row.code === selectedScope);

  // Get all unique endpoints that should be displayed based on the selected scope
  const filteredUniqueEndpoints =
    selectedScope === "all"
      ? uniqueEndpoints
      : uniqueEndpoints.filter((endpoint) =>
          endpoint.scopes.includes(selectedScope)
        );

  // Check if an endpoint is implemented for a PMS and scope
  const isEndpointImplemented = (
    pmsId: string,
    scopeCode: string,
    endpointId: string
  ): boolean => {
    // Find the PMS by ID to get its code
    const pms = pmsList.find((p) => p.id === pmsId);
    if (!pms) return false;

    // Find the endpoint by ID to get its code
    const endpoint = endpoints.find(
      (e) => e.id === endpointId && e.scope.includes(scopeCode)
    );
    if (!endpoint) return false;

    // Check if there's a connectivity for this PMS and endpoint
    return connectivities.some(
      (c) =>
        c.connector_code === pms.code &&
        c.actions_triggers_code === endpoint.code
    );
  };

  // Check if an endpoint is active for a PMS and scope
  const isEndpointActive = (
    pmsId: string,
    scopeCode: string,
    endpointId: string
  ): boolean => {
    // Find the PMS by ID to get its code
    const pms = pmsList.find((p) => p.id === pmsId);
    if (!pms) return false;

    // Find the endpoint by ID to get its code
    const endpoint = endpoints.find(
      (e) => e.id === endpointId && e.scope.includes(scopeCode)
    );
    if (!endpoint) return false;

    // Check if there's an active connectivity for this PMS and endpoint
    const connectivity = connectivities.find(
      (c) =>
        c.connector_code === pms.code &&
        c.actions_triggers_code === endpoint.code
    );

    return connectivity ? connectivity.is_active : false;
  };

  // Check if a trigger is implemented for a PMS and endpoint
  const isTriggerImplemented = (
    pmsId: string,
    scopeCode: string,
    endpointId: string
  ): boolean => {
    // Find the PMS by ID to get its code
    const pms = pmsList.find((p) => p.id === pmsId);
    if (!pms) return false;

    // Find the endpoint by ID to get its code
    const endpoint = endpoints.find(
      (e) => e.id === endpointId && e.scope.includes(scopeCode)
    );
    if (!endpoint) return false;

    // Check if the endpoint code exists in any pms_scopes actions_triggers_code array
    const pmsScopeWithEndpoint = pmsScopes.find(
      (pmsScope) =>
        pmsScope.actions_triggers_code &&
        pmsScope.actions_triggers_code.includes(endpoint.code)
    );

    if (!pmsScopeWithEndpoint) return false;

    // Check if the pms_scope code exists in the trigger_objects array for this PMS
    const connectivity = connectivities.find(
      (c) =>
        c.connector_code === pms.code &&
        c.trigger_objects &&
        c.trigger_objects.includes(pmsScopeWithEndpoint.code)
    );

    return !!connectivity;
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="text-gray-500">Loading PMS integrations...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            PMS API Integrations
          </h2>
        </div>
      </div>

      <div className="extranet-container-body" style={{ overflow: "hidden" }}>
        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-600 text-sm rounded-md">
            {error}
          </div>
        )}

        <div className="mb-6 space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search PMS..."
                  className="form-input pl-10"
                />
                <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-400" />
              <select
                value={selectedScope}
                onChange={(e) => setSelectedScope(e.target.value)}
                className="form-input"
              >
                <option value="all">All Scopes</option>
                <option value="crm">CRM</option>
                <option value="bi">BI</option>
                <option value="rms">RMS</option>
              </select>
            </div>
          </div>
        </div>

        <div className="grid-container">
          <table
            className="min-w-full border-collapse"
            style={{ tableLayout: "fixed", width: "auto" }}
          >
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-gray-50 border-b border-r px-4 py-2 text-left font-medium text-gray-900">
                  PMS / Endpoints
                </th>
                {filteredUniqueEndpoints.map((endpoint) => (
                  <th
                    key={endpoint.id}
                    className="border-b px-2 py-2 text-center font-medium text-gray-700 text-xs whitespace-nowrap bg-gray-50"
                    title={`${endpoint.name} — ${endpoint.method}`}
                  >
                    {endpoint.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* First render scope rows */}
              {filteredScopeRows.map((scopeRow) => (
                <tr key={scopeRow.code} className="scope-row">
                  <td className="sticky left-0 z-10 bg-gray-100 border-b border-r px-4 py-3 font-medium text-gray-800">
                    {/* Leave this cell empty as requested */}
                  </td>

                  {/* For each unique endpoint, check if it belongs to this scope */}
                  {filteredUniqueEndpoints.map((endpoint) => {
                    // Check if this endpoint belongs to this scope
                    const belongsToScope = endpoint.scopes.includes(
                      scopeRow.code
                    );

                    return (
                      <td
                        key={`${scopeRow.code}-${endpoint.id}`}
                        className={`border-b px-2 py-2 text-center ${
                          belongsToScope ? "bg-blue-50" : "bg-gray-50"
                        }`}
                      >
                        {belongsToScope ? (
                          <div className="flex justify-center items-center">
                            <span className="font-medium text-blue-600">
                              {scopeRow.name}
                            </span>
                          </div>
                        ) : (
                          <div className="flex justify-center items-center">
                            <span className="text-gray-300">—</span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}

              {/* Then render PMS rows */}
              {filteredPmsList.map((pms) => (
                <tr key={pms.id}>
                  <td className="sticky left-0 z-10 bg-white border-b border-r px-4 py-3 font-medium">
                    {pms.name}
                  </td>
                  {filteredUniqueEndpoints.map((endpoint) => {
                    // For each endpoint, check if it's implemented for any of the scopes it belongs to
                    const implementedScopes = endpoint.scopes.filter(
                      (scopeCode) =>
                        isEndpointImplemented(pms.id, scopeCode, endpoint.id)
                    );

                    const isImplemented = implementedScopes.length > 0;

                    // Check if any of the implemented scopes are active
                    const activeScopes = endpoint.scopes.filter((scopeCode) =>
                      isEndpointActive(pms.id, scopeCode, endpoint.id)
                    );

                    const isActive = activeScopes.length > 0;

                    // Check if triggers are implemented for any of the scopes
                    const triggerScopes = endpoint.scopes.filter((scopeCode) =>
                      isTriggerImplemented(pms.id, scopeCode, endpoint.id)
                    );

                    const hasTrigger = triggerScopes.length > 0;

                    return (
                      <td
                        key={`${pms.id}-${endpoint.id}`}
                        className={`border-b px-2 py-3 text-center ${
                          hoveredCell &&
                          hoveredCell.pms === pms.id &&
                          hoveredCell.endpoint === endpoint.id
                            ? "bg-blue-50"
                            : ""
                        }`}
                        onMouseEnter={() =>
                          setHoveredCell({
                            pms: pms.id,
                            scope: implementedScopes[0] || "",
                            endpoint: endpoint.id,
                          })
                        }
                        onMouseLeave={() => setHoveredCell(null)}
                      >
                        {isImplemented ? (
                          <div className="flex justify-center items-center gap-1">
                            {isActive ? (
                              <Check className="h-5 w-5 text-green-500" />
                            ) : (
                              <X className="h-5 w-5 text-red-500" />
                            )}
                            {hasTrigger && (
                              <span className="text-xs font-bold text-blue-600">
                                T
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="flex justify-center items-center gap-1">
                            <X className="h-5 w-5 text-red-500" />
                            {hasTrigger && (
                              <span className="text-xs font-bold text-blue-600">
                                T
                              </span>
                            )}
                          </div>
                        )}
                        {hoveredCell &&
                          hoveredCell.pms === pms.id &&
                          hoveredCell.endpoint === endpoint.id && (
                            <div className="absolute z-20 bg-white shadow-lg rounded-md p-3 border mt-1 text-left text-sm">
                              <p className="font-bold">{endpoint.name}</p>
                              <p className="text-gray-600">
                                Method: {endpoint.method}
                              </p>
                              <p className="text-gray-600">PMS: {pms.name}</p>
                              {implementedScopes.length > 0 && (
                                <p className="text-gray-600">
                                  Implemented for:{" "}
                                  {implementedScopes
                                    .map((s) => s.toUpperCase())
                                    .join(", ")}
                                </p>
                              )}
                              <p
                                className={
                                  !isImplemented
                                    ? "text-gray-600"
                                    : isActive
                                    ? "text-green-600"
                                    : "text-red-600"
                                }
                              >
                                {!isImplemented
                                  ? "Not implemented"
                                  : isActive
                                  ? "Active"
                                  : "Inactive"}
                              </p>
                              {hasTrigger && (
                                <p className="text-blue-600">
                                  Triggers implemented for:{" "}
                                  {triggerScopes
                                    .map((s) => s.toUpperCase())
                                    .join(", ")}
                                </p>
                              )}
                            </div>
                          )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Add this to your CSS or use Tailwind's @layer directive
// .grid-container {
//   overflow-x: auto;
//   max-width: 100%;
// }
